class ConfigManager {
    constructor(config) {
        this.config = config
    }

    get(path) {
        return getObjectValue(this.config, path)
    }

    set(path, value) {
        setObjectValue(this.config, path, value)
    }
}

export class ApiProvider {
    static async getPhoneData(string) {
        return await fetch(getConfigValue('api.phone_url') + '/' + string, {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + getConfigValue('key.volpserve-bearer'),
            },
        })
            .then((response) => {
                if (response.ok) {
                    return response.json()
                }
            })
            .then((response) => {
                return response.verified
            })
            .catch((error) => {
                debug(error)

                return false
            })
    }

    static async getZipData(string) {
        try {
            const response = await fetch(getConfigValue('api.zip_url') + '/' + string, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': 'Bearer ' + getConfigValue('key.volpserve-bearer'),
                },
            })

            if (response.ok) {
                return await response.json()
            } else {
                debug('Response not OK:', response.status, response.statusText)

                return {}
            }
        } catch (error) {
            debug('Error during fetch:', error)

            return {}
        }
    }
}

export class LeadForm {
    historyStep = 0

    constructor(config) {
        this.config = new ConfigManager(config)

        this.containerForm = document.querySelector('#' + this.config.get('containerFormID'))
        this.form = this.containerForm.querySelector('form')

        if (this.form === null) {
            debug('Form element not found')

            return
        }

        this.steps = this.determineSteps()

        if (this.steps.length === 0) {
            debug('Form steps not found')

            return
        }

        this.stepFirst = this.determineFirstStep()
        this.stepFinal = this.determineFinalStep()
        this.stepInit = this.config.get('stepInit') ?? this.stepFirst

        this.progressContainer = document.querySelector('#progress-container')
        this.tcpaInfo = document.querySelector('#tcpaInfo')

        this.initialize()
    }

    initialize() {
        window.addEventListener('popstate', this.popStateHandle.bind(this))

        this.stepShow(this.getStepKeyById(this.stepInit))

        // Setup binds
        this.steps.forEach((stepElement) => {
            const stepElementId = stepElement.id
            const bindMethod = stepElementId + 'Bind'

            // If `bind` method exist
            if (typeof this[bindMethod] === 'function') {
                this[bindMethod](stepElement)
            } else {
                debug('Bind not defined for: ' + stepElementId)
            }
        })

        this.form.addEventListener('keypress', async (event) => {
            if (event.keyCode === 13) {
                event.preventDefault()

                const stepSelected = this.getStepSelected()

                // trigger `stepHandle` if form step contains a button action
                if (this.hasStepButton(stepSelected.id) === true) {
                    await this.stepHandle(stepSelected)
                }
            }
        })

        // Set user language
        let inputElement = document.querySelector('#userLanguage')

        if (!inputElement.value) {
            inputElement.value = getUserLanguage()
        }
    }

    determineFirstStep() {
        return getFirstElementFromArray(this.steps).id
    }

    determineFinalStep() {
        return getLastElementFromArray(this.steps).id
    }

    determineSteps() {
        let steps = Array.from(this.form.querySelectorAll('.form-step'))

        const ignoreSteps = (this.config.get('ignoreSteps') ?? '').split(',')

        if (ignoreSteps) {
            steps = steps.filter(function (value) {
                if (ignoreSteps.includes(value.id) === false) {
                    return value
                }
            })
        }

        return steps
    }

    getStepKeyById(id) {
        let stepKey = 0

        this.steps.every((element, key) => {
            if (element.id === id) {
                stepKey = key

                return false
            }

            return true
        })

        return stepKey
    }

    getStepElementByKey(key) {
        return this.steps[key]
    }

    getStepElementByID(id) {
        let stepElement

        this.steps.forEach((element) => {
            if (element.id === id) {
                stepElement = element

                return false
            }

            return true
        })

        return stepElement
    }

    getStepSelected() {
        return this.form.querySelector('.form-step.form-step-show')
    }

    getStepButton(stepElement) {
        return stepElement.querySelector('div.form-command button')
    }

    hasStepButton(stepElementId) {
        const hideButtonOnSteps = (this.config.get('hideButtonOnSteps') ?? '').split(',')

        if (hideButtonOnSteps && hideButtonOnSteps.includes(stepElementId) === true) {
            return false
        }

        return true
    }

    async popStateHandle(event) {
        if (document.location.pathname === this.config.get('resultsURL') || this.historyStep === -1) {
            location.replace(this.config.get('startURL'))

            return
        }

        let goStep = 0
        let directionStep = 'back'

        if (event.state !== null) {
            goStep = event.state.step
        }

        if (this.historyStep < goStep) {
            directionStep = 'forward'
        }

        if (directionStep === 'forward') {
            await this.stepHandle(this.getStepSelected(), true)
        } else {
            this.stepShow(goStep, true)
        }
    }

    async stepHandle(stepElement, doNotSetPushState = false) {
        const buttonElement = this.getStepButton(stepElement)

        // Button is in loading state => do nothing
        if (buttonElement.classList.contains('btn-loading') === true) {
            return
        }

        // Change button to loading
        this.handleStepButtonLoading(stepElement, true)

        // If `check` method exist
        const checkMethod = stepElement.id + 'Check'

        if (typeof this[checkMethod] === 'function') {
            const error = await this[checkMethod](stepElement, true, null, true)

            if (error === false) {
                this.handleStepButtonStatus(stepElement, false)
            } else {
                this.handleStepButtonStatus(stepElement, true)

                // On error disable button loading
                this.handleStepButtonLoading(stepElement, false)
            }
        } else {
            debug('Check not defined for: ' + stepElement.id)

            this.handleStepButtonStatus(stepElement, false)
        }

        // Button is disabled => do nothing
        if (buttonElement.classList.contains('btn-disabled') === true) {
            return
        }

        // If `save` method exist
        const saveMethod = stepElement.id + 'Save'

        if (typeof this[saveMethod] === 'function') {
            this[saveMethod](stepElement)
        }

        const stepElementKey = this.getStepKeyById(stepElement.id)

        if (stepElement.id === this.stepFinal) {
            this.stepProgress(stepElementKey)
            this.handleFormSubmit()
        } else {
            this.handleStepButtonLoading(stepElement, false)

            // Display next step
            this.stepShow(stepElementKey + 1, doNotSetPushState)
        }
    }

    stepShow(stepElementKey, doNotSetPushState = false) {
        // Update `stepElementKey` value based on custom rules
        stepElementKey = this.stepShowRules(stepElementKey)

        // Update `historyStep`
        this.historyStep = stepElementKey

        // Display selected step
        this.steps.forEach((element, key) => {
            if (key === stepElementKey) {
                if (element.classList.contains('form-step-show') === false) {
                    element.classList.add('form-step-show')
                }
            } else {
                element.classList.remove('form-step-show')
            }
        })

        if (doNotSetPushState === false) {
            // Update url
            const url = this.steps[stepElementKey].dataset.url // the url is set via `data-url` attribute

            if (typeof url !== 'undefined' && url) {
                if (url !== window.location.pathname) {
                    // window.history.replaceState({}, null, url)
                    window.history.pushState({
                        'step': stepElementKey
                    }, null, url)
                }
            }
        }

        this.stepProgress(stepElementKey)

        this.stepButtonCreate(stepElementKey)

        if (this.config.get('smoothScrollingTo') === true) {
            smoothScrollingTo(this.form)
        }

        this.stepShowSetup(this.steps[stepElementKey].id)
    }

    hasProgress() {
        return typeof (this.progressContainer) !== 'undefined' && this.progressContainer !== null
    }

    stepProgress(stepElementKey) {
        if (this.hasProgress() === false) {
            return
        }

        // Display progress
        setElementDisplay(this.progressContainer, stepElementKey > 0, 'flex')

        if (stepElementKey === 0) {
            return
        }

        this.updateProgress(stepElementKey)

        let progressBackElement = this.progressContainer.querySelector('#progress-back')

        if (typeof (progressBackElement) !== 'undefined' && progressBackElement !== null) {
            if (stepElementKey === 0) {
                // Hide back button on initial step
                progressBackElement.style.visibility = 'hidden'
            } else {
                // Display back button
                progressBackElement.style.visibility = 'visible'

                // Use `onclick` because only one action can exist (note: addEventListener does not clear previous defined)
                progressBackElement.onclick = (e) => {
                    // Use `data-history` attribute to control how back functionality works
                    if (e.target.dataset.history === 'false') {
                        this.stepShow(stepElementKey - 1)
                    } else {
                        window.history.back()
                    }
                }
            }
        }
    }

    updateProgress(stepElementKey) {
        const minComplete = stepElementKey * 100 / this.steps.length // Calculate progress percentage
        const maxComplete = (stepElementKey + 1) * 100 / this.steps.length // Calculate progress percentage

        const stepComplete = this.calcStepProgress(this.steps[stepElementKey].id)
        const complete = Math.round(minComplete + (maxComplete - minComplete) * stepComplete)

        this.progressContainer.querySelector('#progress-complete').style.width = complete + '%'

        const progressNumberContainer = this.progressContainer.querySelector('#progress-number-container')

        if (typeof (progressNumberContainer) !== 'undefined' && progressNumberContainer !== null) {
            if (complete > 0) {
                progressNumberContainer.querySelector('#progress-number').textContent = complete.toString()
            }

            // Display progress number
            setElementDisplay(progressNumberContainer, complete > 0)
        }
    }

    stepButtonContent(stepSelectedId, is_loading) {
        const defaultContent = {
            buttonFinal: '<span>Complete My Request</span>',
            buttonFirst: '<span>Next</span>',
            buttonNext: '<span>Next</span>',
            buttonLoading: '<i class="fa-solid fa-spinner animate-spin mr-1"></i> Loading...'
        }

        this.config.set('content', { ...defaultContent, ...this.config.get('content') })

        if (is_loading === true) {
            return this.config.get('content.buttonLoading')
        }

        if (stepSelectedId === this.stepFinal) {
            return this.config.get('content.buttonFinal')
        }

        if (stepSelectedId === this.stepFirst) {
            return this.config.get('content.buttonFirst')
        }

        return this.config.get('content.buttonNext')
    }

    stepButtonCreate(stepElementKey) {
        let stepElement = this.getStepElementByKey(stepElementKey)
        let existingButton = this.getStepButton(stepElement)

        // Added for the autofill scenario
        const formElements = stepElement.querySelectorAll('[data-form="control"]')

        let stepValidStatus = true

        for (const formElement of formElements) {
            if (formElement.classList.contains('is-valid') === false) {
                stepValidStatus = false
                break
            }
        }

        // If button already exist do not create it
        if (typeof (existingButton) != 'undefined' && existingButton != null) {
            if (stepValidStatus === true) {
                existingButton.classList.remove('btn-disabled')
            }

            return
        }

        let div = document.createElement('div')
        div.className = 'form-command'

        let button = document.createElement('button')
        button.classList.add('btn-submit')
        button.innerHTML = this.stepButtonContent(stepElement.id, false)

        // Set button as disabled
        if (stepValidStatus === false) {
            button.classList.add('btn-disabled')
        }

        // Hide step button
        if (this.hasStepButton(stepElement.id) === false) {
            button.style.display = 'none'
        }

        button.addEventListener('click', async (e) => {
            e.preventDefault()

            await this.stepHandle(stepElement)
        })

        div.appendChild(button)

        this.stepButtonAppend(stepElement, div)
    }

    stepButtonAppend(stepElement, buttonElement) {
        const buttonPlaceholder = stepElement.querySelector('#' + stepElement.id + 'Button')

        if (typeof (buttonPlaceholder) !== 'undefined' && buttonPlaceholder !== null) {
            buttonPlaceholder.appendChild(buttonElement)
        } else {
            stepElement.appendChild(buttonElement)
        }
    }

    handleStepButtonStatus(stepElement, is_disabled) {
        const buttonElement = this.getStepButton(stepElement)

        if (this.hasProgress() === true) {
            const stepElementKey = this.getStepKeyById(stepElement.id)

            this.updateProgress(stepElementKey)
        }

        if (buttonElement) {
            if (is_disabled === true) {
                buttonElement.classList.add('btn-disabled')
            } else {
                buttonElement.classList.remove('btn-disabled')
            }
        }
    }

    handleStepButtonLoading(stepElement, is_loading) {
        const buttonElement = this.getStepButton(stepElement)
        const stepElementId = stepElement.id

        if (is_loading === false) {
            buttonElement.innerHTML = this.stepButtonContent(stepElementId, false)
            buttonElement.classList.remove('btn-loading')
        } else {
            buttonElement.innerHTML = this.stepButtonContent(stepElementId, true)
            buttonElement.classList.add('btn-loading')
        }

        // Adjust `overlay` display if exist
        this.displayOverlayIfExist(is_loading)
    }

    displayOverlayIfExist(display) {
        let overlay = this.containerForm.querySelector('#form-overlay')

        if (typeof (overlay) !== 'undefined' && overlay !== null) {
            setElementDisplay(overlay, display, 'flex')
        }
    }

    toggleError(inputElement, stepElement, is_valid, with_error) {
        const inputElementId = inputElement.id

        toggleValid(inputElement, is_valid)

        if (is_valid === false) {
            inputElement.focus()

            if (with_error === true) {
                stepElement.querySelector('#' + inputElementId + '_error').style.display = 'block'
            }
        } else {
            stepElement.querySelector('#' + inputElementId + '_error').style.display = 'none'
        }
    }

    isValidStep(fieldIds) {
        let status = true

        for (const id of fieldIds) {
            if (this.form.querySelector('#' + id).classList.contains('is-valid') === false) {
                status = false
                break
            }
        }

        return status
    }

    calcStepProgress(stepElementKey) {
        return 0
    }

    stepShowRules(stepElementKey) {
        return stepElementKey
    }

    toggleElementsBasedOnFormStep(is_hidden) {
        const hideElements = document.querySelectorAll('[data-hidden="true"]')

        for (const hideElement of hideElements) {
            if (is_hidden === true) {
                hideElement.classList.add('hidden')
            } else {
                hideElement.classList.remove('hidden')
            }
        }
    }

    async stepCallback(stepSelectedId) {
    }

    stepShowSetup(stepSelectedId) {
        // Display tcpa info
        setElementDisplay(this.tcpaInfo, stepSelectedId === this.stepFinal)

        // Hide elements depending on form step
        if (stepSelectedId !== this.stepFirst) {
            this.toggleElementsBasedOnFormStep(true)
        }

        this.stepCallback(stepSelectedId)
    }

    handleFormSubmit() {
    }
}
